/**
 * Molecule – ReportCard
 * Collapsible card showing one daily report's details.
 *
 * Props:
 *  - report            DailyReport document (populated hrId for chairman view)
 *  - isChairman        bool – when true shows HR identity + FeedbackBox
 *  - onFeedbackSaved   (report) => void – lift updated report to parent
 */
import React, { useState } from 'react';
import {
  MdExpandMore, MdExpandLess,
  MdPerson, MdCalendarToday, MdWork,
  MdWarning, MdTrendingUp, MdFormatListBulleted
} from 'react-icons/md';
import StatusBadge from '../atoms/StatusBadge';
import TaskTag from '../atoms/TaskTag';
import ConversationBox from './ConversationBox';

const fmt = (dateStr) =>
  new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
  });

const ReportCard = ({ report, isChairman = false, onFeedbackSaved }) => {
  const [open, setOpen] = useState(false);

  const hrName  = report.hrId?.name       || 'HR Employee';
  const hrEmpId = report.hrId?.employeeId || '';

  const handleFeedbackSaved = (updated) => {
    onFeedbackSaved && onFeedbackSaved(updated);
  };

  return (
    <div className="bg-surface rounded-xl border border-border-light shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-background transition-colors"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1.5 text-sm font-semibold text-text-primary">
            <MdCalendarToday size={15} className="text-accent-green flex-shrink-0" />
            {fmt(report.date)}
          </span>

          {isChairman && (
            <span className="flex items-center gap-1.5 text-sm text-text-secondary">
              <MdPerson size={15} className="flex-shrink-0" />
              {hrName}
              {hrEmpId && (
                <span className="text-xs text-text-tertiary">({hrEmpId})</span>
              )}
            </span>
          )}

          {isChairman && <StatusBadge status={report.status} />}

          {report.tasksCompleted?.length > 0 && (
            <span className="text-xs text-text-tertiary">
              {report.tasksCompleted.length} task{report.tasksCompleted.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        <span className="flex-shrink-0 text-text-tertiary ml-2">
          {open ? <MdExpandLess size={20} /> : <MdExpandMore size={20} />}
        </span>
      </button>

      {/* ── Expandable Body ─────────────────────────────────────────────────── */}
      {open && (
        <div className="px-5 pb-5 space-y-4 border-t border-border-light pt-4 animate-slideIn">

          {/* Work Summary */}
          <DetailSection icon={MdWork} label="Work Summary">
            <p className="text-sm text-text-primary whitespace-pre-wrap leading-relaxed">
              {report.workSummary}
            </p>
          </DetailSection>

          {/* Tasks */}
          {report.tasksCompleted?.length > 0 && (
            <DetailSection icon={MdFormatListBulleted} label="Tasks Completed">
              <div className="flex flex-wrap gap-2">
                {report.tasksCompleted.map((t, i) => (
                  <TaskTag key={i} task={t} />
                ))}
              </div>
            </DetailSection>
          )}

          {/* Issues */}
          {report.issuesFaced && (
            <DetailSection icon={MdWarning} label="Issues Faced">
              <p className="text-sm text-text-primary whitespace-pre-wrap leading-relaxed">
                {report.issuesFaced}
              </p>
            </DetailSection>
          )}

          {/* Plan for Tomorrow */}
          {report.planForTomorrow && (
            <DetailSection icon={MdTrendingUp} label="Plan for Tomorrow">
              <p className="text-sm text-text-primary whitespace-pre-wrap leading-relaxed">
                {report.planForTomorrow}
              </p>
            </DetailSection>
          )}

          {/* Conversation Section */}
          <ConversationBox 
            reportId={report._id}
            conversation={report.conversation || []}
            legacyFeedback={report.chairmanFeedback}
            isChairman={isChairman}
            onSaved={handleFeedbackSaved}
          />
        </div>
      )}
    </div>
  );
};

const DetailSection = ({ icon: Icon, label, children }) => (
  <div>
    <p className="flex items-center gap-1.5 text-xs font-semibold text-text-secondary uppercase tracking-wider mb-2">
      <Icon size={13} className="text-accent-green" />
      {label}
    </p>
    {children}
  </div>
);

export default ReportCard;
